import { Badge, Button, Card, CardHeader, EmptyState, Skeleton, formatMoney, parseMoneyInput } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { isApiError } from "../../api/client";
import { listAllItems, type CatalogueItem } from "../../api/catalogue-admin";
import { dryRun, getReport, saveRoles, savePlanItems, validateImport, type ImportJob, type OwnerReport, type PlanItemBody } from "../../api/imports";
import { listRoles } from "../../api/roles";
import { useServices } from "../../app/services";
import { Load, useScope } from "../settings/kit";
import s from "../settings/settings.module.css";
import { downloadText, issuesCsv, rejectedRowsCsv } from "./csv";
import o from "./onboarding.module.css";
import { StepError, useImportScope } from "./shared";
import { OPERATION_ORDER, dryRunBlockers, operationLabel } from "./state";
import { t } from "./strings";

/** Step 3a: the data is mapped but not yet read. One press runs the pipeline on the server. */
export function CheckStep({ job, onReload, onBack }: { job: ImportJob; onReload: () => void; onBack: () => void }) {
  const { client } = useServices();
  const sc = useImportScope(job.id);
  const validate = useMutation({ mutationFn: () => validateImport(client, job.id), onSuccess: (r) => { sc.qc.setQueryData(sc.reportKey, { status: r.job.status, report: r.report }); sc.putJob(r.job); } });
  return (
    <Card aria-label={t.steps.check.title}>
      <CardHeader title={t.steps.check.title} subtitle={t.steps.check.body} />
      <StepError error={validate.error} onReload={onReload} />
      <div className={s.bar}>
        <Button onClick={onBack}>Back to columns</Button>
        <Button variant="primary" busy={validate.isPending} busyLabel="Checking…" onClick={() => validate.mutate()}>Check the data</Button>
      </div>
    </Card>
  );
}

const PAGE = 100;

/** Step 3b: the report. Counts, warnings, errors with row numbers, CSVs, plan and role matching, then the rehearsal. */
export function ReviewStep({ job, onReload, onEdit }: { job: ImportJob; onReload: () => void; onEdit: (to: "upload" | "mapping") => void }) {
  const { client } = useServices();
  const sc = useImportScope(job.id);
  const report = useQuery({ queryKey: sc.reportKey, queryFn: () => getReport(client, job.id), retry: false });
  const rehearse = useMutation({
    mutationFn: () => dryRun(client, job.id),
    onSuccess: (r) => { sc.qc.setQueryData(sc.reportKey, { status: r.job.status, report: r.report }); sc.putJob(r.job); },
    // A failed rehearsal still moves the job (dry_run_failed): re-read it so the wizard follows.
    onSettled: () => void sc.qc.invalidateQueries({ queryKey: sc.jobKey }),
  });
  return (
    <Load query={report} what="the report" skeleton={<Card aria-label="Loading report"><div className={s.skel} aria-busy="true"><Skeleton height={60} /><Skeleton height={34} /><Skeleton height={34} /></div></Card>}>
      {({ report: r }) => (
        <div className={s.page}>
          <Summary job={job} r={r} />
          <Problems r={r} />
          <Names job={job} r={r} onReload={onReload} />
          <Card aria-label="Rehearse">
            <CardHeader title={t.steps.rehearse.title} subtitle={t.steps.rehearse.body} />
            <Blockers r={r} />
            <StepError error={rehearse.error} onReload={onReload} />
            <div className={s.bar}>
              <span className={s.acts}><Button onClick={() => onEdit("upload")}>Change sheets</Button><Button onClick={() => onEdit("mapping")}>Change columns</Button></span>
              <Button variant="primary" busy={rehearse.isPending} busyLabel="Rehearsing…" disabled={dryRunBlockers(r).length > 0} onClick={() => rehearse.mutate()}>Run the rehearsal</Button>
            </div>
          </Card>
        </div>
      )}
    </Load>
  );
}

function Blockers({ r }: { r: OwnerReport }) {
  const list = dryRunBlockers(r);
  if (list.length === 0) return null;
  return <div className={s.formErr} role="alert"><b>Before the rehearsal:</b><ul>{list.map((b) => <li key={b}>{b}</li>)}</ul></div>;
}

export function Summary({ job, r }: { job: ImportJob; r: OwnerReport }) {
  const sc = useScope();
  const cur = sc.ctx.me?.business.currency ?? "INR";
  const loc = sc.ctx.locale;
  const ops = OPERATION_ORDER.filter((k) => (r.operations[k] ?? 0) > 0);
  const stat = (n: string, label: string) => <div key={label} className={o.stat}><b>{n}</b><span>{label}</span></div>;
  return (
    <Card aria-label="What the cutover will write">
      <CardHeader title={t.steps.review.title} subtitle={`${t.steps.review.body} ${job.label}.`} action={r.canProceed ? <Badge tone="ok">Ready</Badge> : <Badge tone="crit">Needs attention</Badge>} />
      <div className={o.stats} role="list" aria-label="Counts to be written">
        {ops.length === 0 ? <EmptyState compact icon="receipt" title="Nothing to write" description="No row survived the check." /> : null}
        {ops.map((k) => <div key={k} role="listitem" className={o.stat}><b className={o.count}>{(r.operations[k] ?? 0).toLocaleString(loc)}</b><span>{operationLabel(k)}</span></div>)}
        {r.skipped.total > 0 ? stat(r.skipped.total.toLocaleString(loc), "Rows skipped") : null}
        {r.duesTotalPaise > 0 ? stat(formatMoney(r.duesTotalPaise, cur, loc), "Dues being brought over") : null}
        {r.plansEndingWithin30Days > 0 ? stat(r.plansEndingWithin30Days.toLocaleString(loc), "Plans ending within 30 days") : null}
      </div>
      <div className={s.note}>Imported plans have no invoice and no payment in Helmr, so they <b>cannot be refunded or upgraded with credit</b>. To change one, cancel it and sell a new plan. You can still freeze, extend, transfer and renew them.</div>
      <div className={o.hash} style={{ padding: "0 16px 14px" }}>Plan key: {r.planKey}</div>
    </Card>
  );
}

export function Problems({ r }: { r: OwnerReport }) {
  const [shown, setShown] = useState(PAGE);
  const codes = (b: Record<string, number>) => Object.entries(b).sort((a, c) => c[1] - a[1]);
  if (r.errors.total === 0 && r.warnings.total === 0 && r.skipped.total === 0 && r.ambiguousDates.total === 0) {
    return <Card aria-label="Problems"><CardHeader title="No problems found" subtitle="Every row passed the check." /></Card>;
  }
  return (
    <Card aria-label="Problems">
      <CardHeader title="Problems" subtitle={`${r.errors.total.toLocaleString()} error(s), ${r.warnings.total.toLocaleString()} warning(s)${r.onError === "skip_rows" ? ". Rows with errors are skipped." : ". Any error stops the import."}`}
        action={<span className={s.acts}>
          {r.skipped.total > 0 ? <Button size="sm" onClick={() => downloadText("rejected-rows.csv", rejectedRowsCsv(r))}>Download rejected rows (CSV)</Button> : null}
          <Button size="sm" onClick={() => downloadText("import-problems.csv", issuesCsv(r))}>Download all problems (CSV)</Button>
        </span>} />
      {r.ambiguousDates.total > 0 ? (
        <div className={s.formErr} role="alert" style={{ background: "var(--color-status-warn-bg)", color: "var(--color-status-warn-tx)" }}>
          <b>Check the date order.</b> A wrong day/month order shifts every expiry date.
          <ul>{r.ambiguousDates.columns.map((c) => <li key={`${c.file}|${c.entity}|${c.field}`}>{c.file}, column “{c.column}”: {c.count.toLocaleString()} date(s) could be read either way</li>)}</ul>
        </div>
      ) : null}
      {codes(r.errors.byCode).length > 0 || codes(r.warnings.byCode).length > 0 ? (
        <div className={s.pad}><div className={s.checks}>
          {codes(r.errors.byCode).map(([c, n]) => <Badge key={`e${c}`} tone="crit">{c} × {n}</Badge>)}
          {codes(r.warnings.byCode).map(([c, n]) => <Badge key={`w${c}`} tone="warn">{c} × {n}</Badge>)}
        </div></div>
      ) : null}
      {r.issues.length > 0 ? (
        <div className={o.tableWrap}>
          <table aria-label="Problems by row">
            <thead><tr><th>File</th><th>Row</th><th>Problem</th></tr></thead>
            <tbody>{r.issues.slice(0, shown).map((i, k) => <tr key={k}><td>{i.file}</td><td>{i.row}</td><td><Badge tone={i.severity === "error" ? "crit" : "warn"}>{i.severity}</Badge> {i.message}</td></tr>)}</tbody>
          </table>
        </div>
      ) : null}
      <div className={s.bar}>
        <span className={s.hint}>{r.issuesTruncated ? `Showing the first ${r.issues.length.toLocaleString()} of ${(r.errors.total + r.warnings.total).toLocaleString()}; fix these and check again for the rest.` : `${Math.min(shown, r.issues.length).toLocaleString()} of ${r.issues.length.toLocaleString()} shown`}</span>
        {shown < r.issues.length ? <Button size="sm" onClick={() => setShown((n) => n + PAGE)}>Load more</Button> : null}
      </div>
    </Card>
  );
}

/* ---------- plan and role names ---------- */

type Choice = { itemId: string } | { create: { duration: string; sessions: string; price: string } } | null;
const NEW = "__new__";

function Names({ job, r, onReload }: { job: ImportJob; r: OwnerReport; onReload: () => void }) {
  const { client } = useServices();
  const sc = useImportScope(job.id);
  const qc = useQueryClient();
  const hasPlans = r.unmappedPlanNames.length > 0;
  const hasRoles = r.unmappedRoleNames.length > 0;
  const canRoles = sc.ctx.has("roles.roles_view");
  const canCreate = sc.ctx.has("catalogue.plans_add");
  const items = useQuery({ queryKey: sc.key("imports", "catalogue"), queryFn: () => listAllItems(client), enabled: hasPlans });
  const roles = useQuery({ queryKey: sc.key("imports", "roles"), queryFn: () => listRoles(client), enabled: hasRoles && canRoles });
  const [plans, setPlans] = useState<Record<string, Choice>>({});
  const [roleSel, setRoleSel] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const cur = sc.ctx.me?.business.currency ?? "INR";

  // Saving returns the job to draft; check again straight away so the report shows what is still unmatched.
  const save = useMutation({
    mutationFn: async () => {
      setFormError(null);
      const body: PlanItemBody[] = [];
      for (const p of r.unmappedPlanNames) {
        const c = plans[`${p.entity}|${p.name}`];
        if (!c) continue;
        if ("itemId" in c) body.push({ entity: p.entity, sourceName: p.name, itemId: c.itemId });
        else {
          const price = c.create.price.trim() === "" ? { ok: true as const, minor: 0 } : parseMoneyInput(c.create.price, cur);
          const days = Number(c.create.duration);
          const sessions = Number(c.create.sessions);
          if (!price.ok) throw new Error(`Price for “${p.name}” is not a valid amount.`);
          if (!Number.isInteger(days) || days < 1) throw new Error(`Give a validity in days for “${p.name}”.`);
          if (p.entity === "pt_balances" && (!Number.isInteger(sessions) || sessions < 1)) throw new Error(`Give the number of sessions for “${p.name}”.`);
          body.push({ entity: p.entity, sourceName: p.name, create: { name: p.name, price: price.minor, durationDays: days, ...(p.entity === "pt_balances" ? { sessionsTotal: sessions } : {}) } });
        }
      }
      if (body.length > 0) await savePlanItems(client, job.id, body);
      const roleBody = r.unmappedRoleNames.filter((x) => roleSel[x.name]).map((x) => ({ sourceName: x.name, roleId: roleSel[x.name]! }));
      if (roleBody.length > 0) await saveRoles(client, job.id, roleBody);
      return validateImport(client, job.id);
    },
    onSuccess: (v) => { qc.setQueryData(sc.reportKey, { status: v.job.status, report: v.report }); sc.putJob(v.job); setPlans({}); setRoleSel({}); },
    onError: (e) => { if (!isApiError(e)) setFormError(e instanceof Error ? e.message : "Something went wrong."); },
  });

  if (!hasPlans && !hasRoles) return null;
  const chosen = Object.values(plans).some(Boolean) || Object.values(roleSel).some(Boolean);
  return (
    <Card aria-label="Match plans and roles">
      <CardHeader title="Match plan and role names" subtitle="These names appear in your sheet but not in Helmr. Pick the Helmr plan or role each one means." />
      {r.unmappedPlanNames.map((p) => {
        const key = `${p.entity}|${p.name}`;
        const kind = p.entity === "memberships" ? "membership" : "pt_pack";
        const options = (items.data ?? []).filter((i: CatalogueItem) => i.kind === kind && i.active && i.archivedAt === null);
        const c = plans[key] ?? null;
        const value = c === null ? "" : "itemId" in c ? c.itemId : NEW;
        return (
          <div key={key} className={o.nameRow}>
            <span className={o.src}><b>{p.name}</b><small>{p.entity === "memberships" ? "Membership plan" : "PT pack"} · {t.rows(p.rows)}</small></span>
            <span style={{ display: "grid", gap: 6 }}>
              <select className={s.select} aria-label={`Helmr plan for ${p.name}`} value={value} disabled={items.isPending}
                onChange={(e) => setPlans((m) => ({ ...m, [key]: e.target.value === "" ? null : e.target.value === NEW ? { create: { duration: "", sessions: "", price: "" } } : { itemId: e.target.value } }))}>
                <option value="">Choose a plan…</option>
                {options.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                {canCreate ? <option value={NEW}>Create “{p.name}” as a new plan…</option> : null}
              </select>
              {c && "create" in c ? (
                <span className={s.checks}>
                  <input className={s.input} style={{ width: 130 }} inputMode="numeric" aria-label={`Days a ${p.name} plan lasts`} placeholder="Valid for (days)" value={c.create.duration} onChange={(e) => setPlans((m) => ({ ...m, [key]: { create: { ...c.create, duration: e.target.value } } }))} />
                  {p.entity === "pt_balances" ? <input className={s.input} style={{ width: 120 }} inputMode="numeric" aria-label={`Sessions in a ${p.name} pack`} placeholder="Sessions" value={c.create.sessions} onChange={(e) => setPlans((m) => ({ ...m, [key]: { create: { ...c.create, sessions: e.target.value } } }))} /> : null}
                  <input className={s.input} style={{ width: 120 }} inputMode="decimal" aria-label={`Price of a ${p.name} plan`} placeholder="Price" value={c.create.price} onChange={(e) => setPlans((m) => ({ ...m, [key]: { create: { ...c.create, price: e.target.value } } }))} />
                </span>
              ) : null}
            </span>
          </div>
        );
      })}
      {hasRoles && !canRoles ? <div className={s.note}>Matching staff roles needs access to Roles. Ask the owner to do this step.</div> : null}
      {canRoles ? r.unmappedRoleNames.map((x) => (
        <div key={x.name} className={o.nameRow}>
          <span className={o.src}><b>{x.name}</b><small>Staff role · {t.rows(x.rows)}</small></span>
          <select className={s.select} aria-label={`Helmr role for ${x.name}`} value={roleSel[x.name] ?? ""} disabled={roles.isPending} onChange={(e) => setRoleSel((m) => ({ ...m, [x.name]: e.target.value }))}>
            <option value="">Choose a role…</option>
            {(roles.data ?? []).filter((ro) => !ro.isOwner).map((ro) => <option key={ro.id} value={ro.id}>{ro.name}</option>)}
          </select>
        </div>
      )) : null}
      {formError ? <div className={s.formErr} role="alert">{formError}</div> : null}
      <StepError error={save.error instanceof Error && !isApiError(save.error) ? null : save.error} onReload={onReload} />
      <div className={s.bar}>
        <span className={s.hint}>Saving checks the data again.</span>
        <Button variant="primary" busy={save.isPending} busyLabel="Saving…" disabled={!chosen} onClick={() => save.mutate()}>Save matches</Button>
      </div>
    </Card>
  );
}
